import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';

import { DialogService } from '../../../../services/dialog.service';
import { GroupsService } from '../../../../services/groups.service';
import { FlashMessageService } from '../../../../services/flash-message.service';

import { User } from '../../../../structures/user';
import { Group } from '../../../../structures/group';

@Component({
    selector: 'app-group-entry',
    templateUrl: './group-entry.component.html',
    styleUrls: ['./group-entry.component.scss'],
    standalone: false
})
export class GroupEntryComponent implements OnInit {
    constructor(
        private dialogService: DialogService,
        private groupsService: GroupsService,
        private flashMessageService: FlashMessageService
    ) {}

    subscriptions$: Subscription = new Subscription();

    @Input() public group: Group;
    @Output() toggled: EventEmitter<void> = new EventEmitter();

    ngOnInit() {}

    getGroupIcon(): string {
        return this.group.type === 'teacher' ? 'icon-formateurinterne' : 'icon-groupe';
    }

    getUsers(): void {
        if (!this.group.users) {
            this.subscriptions$.add(
                this.groupsService.getGroup(this.group.id).subscribe((groupReceived: Group) => {
                    this.group = {
                        ...this.group,
                        ...groupReceived,
                        users: groupReceived.users.sort((a, b) =>
                            a.lastname.toUpperCase().localeCompare(b.lastname.toUpperCase())
                        )
                    };
                })
            );
        }
    }

    getGroupCounter(): string {
        return `(${this.group.users.length} ${
            this.group.type === 'teacher' ? 'formateurs' : 'apprenants'
        })`;
    }

    getUserIcon(user: User): string {
        if (user.roles.prospect) {
            return 'icon-apprenant-prospect';
        } else if (user.roles.learner) {
            return 'icon-apprenant';
        } else if (user.roles.internalTeacher) {
            return 'icon-formateurinterne';
        } else if (user.roles.externalTeacher) {
            return 'icon-formateurexterne';
        } else if (user.roles.siteTeacher) {
            return 'icon-formateur-site';
        } else if (user.roles.nationalTeacher) {
            return 'icon-auteurnational';
        } else if (user.roles.corporationTeacher) {
            return 'icon-FormateurEntreprise';
        } else if (user.roles.tutor) {
            return 'icon-tuteurentreprise';
        } else if (user.roles.localAdmin) {
            return 'icon-adminlocal';
        } else if (user.roles.nationalAdmin) {
            return 'icon-adminnational';
        } else if (user.roles.externalCallManager) {
            return 'icon-Telephone';
        }
    }

    sendReminder($event: Event) {
        $event.stopImmediatePropagation();
        this.groupsService.sendEmailReminder(this.group.id).subscribe(() => {
            this.flashMessageService.flash("L'email a été renvoyé à tous les membres du groupe");
        });
    }

    /**
     * Toggle the activation state of the group
     *
     * @description A deactivated group is hidden in Training and Cloud while its members
     * and its history are left untouched. The click must not reach the expansion panel,
     * which would otherwise expand the group. Emits `toggled` so the parent list can
     * reload: the group may no longer match the selected "Etat du groupe" filter.
     * @param {Event} $event Click event on the action icon
     */
    toggleGroup($event: Event): void {
        $event.stopImmediatePropagation();
        const isDisabled = this.group.disabled === true;

        this.dialogService
            .openConfirmationDialog(
                `${isDisabled ? 'Activer' : 'Désactiver'} le groupe`,
                `Êtes-vous sûr de vouloir ${isDisabled ? 'activer' : 'désactiver'} le groupe ${
                    this.group.name
                } ?`
            )
            .subscribe((confirmation: boolean) => {
                if (!confirmation) {
                    return;
                }
                const request = isDisabled
                    ? this.groupsService.enableGroup(this.group)
                    : this.groupsService.disableGroup(this.group);

                this.subscriptions$.add(
                    request.subscribe(() => {
                        this.group.disabled = !isDisabled;
                        this.flashMessageService.flash(
                            `Le groupe <strong>${this.group.name}</strong> a été ${
                                isDisabled ? 'activé' : 'désactivé'
                            }.`
                        );
                        this.toggled.emit();
                    })
                );
            });
    }

    editGroup($event: Event) {
        $event.stopImmediatePropagation();
        this.dialogService.openCreateGroup(this.group).subscribe(() => {
            this.subscriptions$.add(
                this.groupsService.getGroup(this.group.id).subscribe((groupReceived: Group) => {
                    this.group.users = [];
                    this.group = groupReceived;
                })
            );
        });
    }
}
